export const tvAssetSourceEnum = {
  parent: 'parent',
  iframe: 'iframe',
  both: 'both',
  unavailable: 'unavailable',
} as const;

export type TvAssetSource = (typeof tvAssetSourceEnum)[keyof typeof tvAssetSourceEnum];

export const tvAssetCacheResultEnum = {
  hit: 'hit',
  miss: 'miss',
  unknown: 'unknown',
} as const;

export type TvAssetCacheResult = (typeof tvAssetCacheResultEnum)[keyof typeof tvAssetCacheResultEnum];

export interface TvAssetResourceTiming {
  name: string;
  startTime: number;
  responseEnd: number;
  transferSize: number;
  decodedBodySize: number;
  deliveryType?: string;
}

export interface TvAssetTimingWindow {
  startTime: number;
  endTime: number;
}

export interface TvAssetTimingSource {
  entries: TvAssetResourceTiming[];
  timeOrigin?: number;
}

export interface TvAssetTimingMetrics {
  tv_asset_count: number;
  tv_asset_cache_hit_count: number;
  tv_asset_cache_miss_count: number;
  tv_asset_unknown_count: number;
  tv_asset_transfer_bytes: number;
  tv_asset_download_ms: number;
  tv_asset_source: TvAssetSource;
}

const roundMilliseconds = (value: number) => Math.round(value * 100) / 100;

export const isTvAssetUrl = (name: string): boolean => {
  try {
    const pathname = new URL(name, 'https://placeholder.invalid').pathname;
    return pathname.includes('/charting_library/') || pathname.includes('/datafeeds/');
  } catch {
    return false;
  }
};

export const isTvAssetResourceTiming = (entry: PerformanceEntry): entry is PerformanceResourceTiming => {
  if (entry.entryType !== 'resource') return false;
  const resource = entry as PerformanceResourceTiming;
  return typeof resource.transferSize === 'number' && typeof resource.decodedBodySize === 'number';
};

export const toTvAssetResourceTiming = (entry: PerformanceResourceTiming): TvAssetResourceTiming => ({
  name: entry.name,
  startTime: entry.startTime,
  responseEnd: entry.responseEnd,
  transferSize: entry.transferSize,
  decodedBodySize: entry.decodedBodySize,
  deliveryType: 'deliveryType' in entry ? (entry as TvAssetResourceTiming).deliveryType : undefined,
});

export const classifyTvAssetCache = (entry: TvAssetResourceTiming): TvAssetCacheResult => {
  if (entry.deliveryType === 'cache') return tvAssetCacheResultEnum.hit;
  if (entry.transferSize > 0) return tvAssetCacheResultEnum.miss;
  if (entry.transferSize === 0 && entry.decodedBodySize > 0) return tvAssetCacheResultEnum.hit;
  return tvAssetCacheResultEnum.unknown;
};

export const shiftResourceToParentClock = (
  entry: TvAssetResourceTiming,
  sourceTimeOrigin: number | undefined,
  parentTimeOrigin: number,
): TvAssetResourceTiming => {
  if (sourceTimeOrigin == null) return entry;
  const delta = sourceTimeOrigin - parentTimeOrigin;
  if (delta === 0) return entry;
  return {
    ...entry,
    startTime: entry.startTime + delta,
    responseEnd: entry.responseEnd + delta,
  };
};

const overlapsWindow = (entry: TvAssetResourceTiming, window: TvAssetTimingWindow) => {
  const start = Math.max(entry.startTime, window.startTime);
  const end = Math.min(entry.responseEnd, window.endTime);
  return end > start;
};

export const boundingBoxDownloadMs = (entries: TvAssetResourceTiming[], window: TvAssetTimingWindow): number => {
  let minStart = Number.POSITIVE_INFINITY;
  let maxEnd = Number.NEGATIVE_INFINITY;

  for (const entry of entries) {
    const start = Math.max(entry.startTime, window.startTime);
    const end = Math.min(entry.responseEnd, window.endTime);
    if (end <= start) continue;
    minStart = Math.min(minStart, start);
    maxEnd = Math.max(maxEnd, end);
  }

  if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd) || maxEnd <= minStart) return 0;
  return roundMilliseconds(maxEnd - minStart);
};

const preferCompleteTiming = (current: TvAssetResourceTiming, candidate: TvAssetResourceTiming) => {
  const currentKnown = current.decodedBodySize + current.transferSize;
  const candidateKnown = candidate.decodedBodySize + candidate.transferSize;
  return candidateKnown > currentKnown ? candidate : current;
};

export const aggregateTvAssetTiming = ({
  parent,
  iframe,
  window,
  parentTimeOrigin,
}: {
  parent?: TvAssetTimingSource;
  iframe?: TvAssetTimingSource | 'unavailable';
  window: TvAssetTimingWindow;
  parentTimeOrigin: number;
}): TvAssetTimingMetrics => {
  const parentReadable = parent != null;
  const iframeReadable = iframe != null && iframe !== 'unavailable';
  const tv_asset_source: TvAssetSource = parentReadable
    ? iframeReadable
      ? tvAssetSourceEnum.both
      : tvAssetSourceEnum.parent
    : iframeReadable
      ? tvAssetSourceEnum.iframe
      : tvAssetSourceEnum.unavailable;

  if (tv_asset_source === tvAssetSourceEnum.unavailable) {
    return {
      tv_asset_count: 0,
      tv_asset_cache_hit_count: 0,
      tv_asset_cache_miss_count: 0,
      tv_asset_unknown_count: 0,
      tv_asset_transfer_bytes: 0,
      tv_asset_download_ms: 0,
      tv_asset_source,
    };
  }

  const byName = new Map<string, TvAssetResourceTiming>();
  const ingest = (source: TvAssetTimingSource | undefined) => {
    if (!source) return;
    for (const entry of source.entries) {
      if (!isTvAssetUrl(entry.name)) continue;
      const shifted = shiftResourceToParentClock(entry, source.timeOrigin, parentTimeOrigin);
      if (!overlapsWindow(shifted, window)) continue;
      const existing = byName.get(shifted.name);
      byName.set(shifted.name, existing ? preferCompleteTiming(existing, shifted) : shifted);
    }
  };

  ingest(parent);
  if (iframeReadable) ingest(iframe);

  const overlapping = [...byName.values()];
  let hit = 0;
  let miss = 0;
  let unknown = 0;
  let transferBytes = 0;

  for (const entry of overlapping) {
    const cache = classifyTvAssetCache(entry);
    if (cache === tvAssetCacheResultEnum.hit) hit += 1;
    else if (cache === tvAssetCacheResultEnum.miss) miss += 1;
    else unknown += 1;
    transferBytes += entry.transferSize;
  }

  return {
    tv_asset_count: overlapping.length,
    tv_asset_cache_hit_count: hit,
    tv_asset_cache_miss_count: miss,
    tv_asset_unknown_count: unknown,
    tv_asset_transfer_bytes: transferBytes,
    tv_asset_download_ms: boundingBoxDownloadMs(overlapping, window),
    tv_asset_source,
  };
};

export const shouldAttachTvAssetTiming = (trigger: string): boolean =>
  trigger === 'initial' || trigger === 'tab_return';
